import React, { useState } from "react";
import Button from "../widgets/Button";
import routes from "../routes/routes";
import { useNavigate } from "react-router-dom";
import {
  getUsername,
  hasUserAccess,
} from "../services/CommonService";
import { getExchangeConfig } from "../helper/platformConfig";
import {
  INVENTORY_GET_ROLE,
  TRADER_ACCESS_ROLE,
  TRANSACTIONS_ACCESS_ROLE,
} from "../constants/roles";
import { Oval } from "react-loader-spinner";
import { useAuth0 } from "@auth0/auth0-react";

const Sidebar = ({ menu, setMenu }) => {
  const navigate = useNavigate();
  const [omsLoading, setOmsLoading] = useState(false);

  /*
   * Auth0
   */
  const {
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  /*
   * Logout
   */
  const logout = () => {
    auth0Logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`,
      },
    });
  };

  /*
   * Open OMS
   *
   * Auth0 access token is retrieved using
   * getAccessTokenSilently().
   */
  const handleOmsClick = async () => {
    if (omsLoading) {
      return;
    }

    setMenu(false);
    setOmsLoading(true);

    let newWindow = null;

    try {
      /*
       * Open the new window immediately.
       *
       * This prevents the browser popup blocker from
       * blocking the OMS window because token retrieval
       * is asynchronous.
       */
      newWindow = window.open("", "_blank");

      if (!newWindow) {
        console.error(
          "Failed to open new window. Please allow popups."
        );

        return;
      }

      /*
       * Get Auth0 access token.
       */
      const token = await getAccessTokenSilently();

      if (!token) {
        console.error("No Auth0 access token available.");

        newWindow.close();
        return;
      }

      /*
       * OMS URL
       */
      const omsBaseUrl =
        window.env.OMS_PORTAL_LOGIN_BASE_URL;

      const omsUrl =
        `${omsBaseUrl}/api/order/portalLogin`;

      /*
       * Escape token before inserting it into HTML.
       */
      const escapedToken = token
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      /*
       * Create a form that POSTs the Auth0 token to OMS.
       */
      const formHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Redirecting to OMS...</title>
          </head>

          <body>
            <p>Redirecting to OMS...</p>

            <form
              id="omsForm"
              method="POST"
              action="${omsUrl}"
            >
              <input
                type="hidden"
                name="accessToken"
                value="${escapedToken}"
              />
            </form>

            <script>
              document.getElementById("omsForm").submit();
            </script>
          </body>
        </html>
      `;

      /*
       * Write the form to the new window.
       */
      newWindow.document.write(formHtml);
      newWindow.document.close();

    } catch (error) {
      console.error(
        "Unable to login to OMS using Auth0 token:",
        error
      );

      /*
       * Close the blank window if something failed
       * before the OMS redirect.
       */
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
    } finally {
      setOmsLoading(false);
    }
  };

  /*
   * Base menu items
   */
  const menuList = [
    {
      href: routes.listing,
      title: getExchangeConfig().sidebarLabels[0],
    },
    {
      href: routes.documents,
      title: getExchangeConfig().sidebarLabels[1],
    },
    {
      href: routes.holding,
      title: getExchangeConfig().sidebarLabels[2],
    },
  ];

  /*
   * Transactions access
   */
  if (
    hasUserAccess([TRANSACTIONS_ACCESS_ROLE])
  ) {
    menuList.push({
      href: routes.transactions,
      title: getExchangeConfig().sidebarLabels[3],
    });
  }

  /*
   * Inventory access
   */
  if (
    hasUserAccess([INVENTORY_GET_ROLE])
  ) {
    menuList.push({
      href: routes.inventory,
      title: getExchangeConfig().sidebarLabels[4],
    });

    menuList.push({
      href: routes.retirementRequests,
      title: getExchangeConfig().sidebarLabels[5],
    });

    menuList.push({
      href: routes.retirementHistory,
      title: getExchangeConfig().sidebarLabels[6],
    });
  }

  /*
   * OMS access
   */
  const showOmsButton = hasUserAccess([
    TRADER_ACCESS_ROLE,
  ]);

  return (
    <aside
      className={`${
        menu
          ? "max-lg:left-0"
          : "max-lg:-left-full"
      } max-lg:absolute relative px-4 py-5 2xl:py-8 flex flex-col justify-between h-full w-64 2xl:w-72 bg-foreground transition-all z-50`}
    >
      <div className="flex flex-col gap-3 2xl:gap-4">

        {/* Main navigation */}
        {menuList.map((items, i) => (
          <Button
            key={i}
            title={items.title}
            onClick={() => {
              setMenu(false);
              navigate(items.href);
            }}
            size="lg"
          />
        ))}

        {/* OMS */}
        {showOmsButton && (
          <Button
            title={
              omsLoading
                ? "Loading..."
                : "OMS"
            }
            onClick={handleOmsClick}
            size="lg"
            isDisabled={omsLoading}
            icon={
              omsLoading ? (
                <Oval
                  height={16}
                  width={16}
                  color="#ffffff"
                  wrapperStyle={{
                    display: "inline-block",
                  }}
                  visible={true}
                  ariaLabel="loading"
                />
              ) : null
            }
          />
        )}
      </div>

      {/* Bottom section */}
      <div className="flex flex-col gap-3">

        {/* Logout */}
        <Button
          title="Logout"
          size="lg"
          onClick={logout}
        />

        {/* Logged-in user */}
        <div>
          <p className="text-sm text-black text-center w-full line-clamp-2">
            Logged in as{" "}
            <span className="capitalize">
              {getUsername()}
            </span>
          </p>
        </div>

      </div>
    </aside>
  );
};

export default Sidebar;